/**
 * Mobile drill-down menu (build spec §4.4): departments → aisles → shelves.
 *
 * Departments and aisles come from the Browse aisles panel markup already in the page; shelves
 * load from the `cfs-menu-panel` section through the same cache as the desktop panel. Each level
 * starts with a highlighted "Shop all {name}" link. The menu is a <dialog> opened as a popover
 * (Esc, the Android back gesture and light dismiss close it; Tab is kept inside; the page behind
 * is hidden from screen readers); when the level changes focus moves to the level title, and Back
 * returns focus to the row that opened the level.
 *
 * CFS: a popover, not showModal(). A modal dialog makes the rest of the page inert, which restyles
 * every element on each open and close (~100 ms on a mid-range phone); a popover only restyles
 * itself. Browsers without popover support still get showModal().
 */
import { loadDepartmentDetail } from '@theme/cfs-aisles-panel';

const MOBILE = window.matchMedia('(max-width: 989px)');
const NOT_PAGE_CONTENT = /^(SCRIPT|STYLE|LINK|TEMPLATE|META|NOSCRIPT)$/;

/**
 * @typedef {{ title: string, href: string }} MenuLink
 * @typedef {{ level: 0 | 1 | 2, department?: number, aisle?: number, opener?: HTMLElement | null }} MenuState
 */

class CfsMobileMenu extends HTMLElement {
  /** @type {MenuState[]} */
  #stack = [];
  /** @type {HTMLElement | null} */
  #trigger = null;
  #renderId = 0;
  #popover = 'popover' in HTMLElement.prototype;
  /** @type {[Element, string | null][]} page elements hidden while the menu is open, with their old aria-hidden */
  #hidden = [];
  /** @type {Element | null} the compact header bar, hidden from screen readers while the menu is open */
  #compact = null;

  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement} */ (this.querySelector('[data-cfs-mm-dialog]'));
    this.list = /** @type {HTMLElement} */ (this.querySelector('[data-cfs-mm-list]'));
    this.titleEl = /** @type {HTMLElement} */ (this.querySelector('[data-cfs-mm-title]'));
    this.trail = /** @type {HTMLElement} */ (this.querySelector('[data-cfs-mm-trail]'));
    this.all = /** @type {HTMLAnchorElement} */ (this.querySelector('[data-cfs-mm-all]'));
    this.allLabel = /** @type {HTMLElement} */ (this.querySelector('[data-cfs-mm-all-label]'));
    this.back = /** @type {HTMLButtonElement} */ (this.querySelector('[data-cfs-mm-back]'));
    this.backLabel = /** @type {HTMLElement} */ (this.querySelector('[data-cfs-mm-back-label]'));
    this.skeleton = /** @type {HTMLElement} */ (this.querySelector('[data-cfs-mm-skeleton]'));
    /** @type {HTMLTemplateElement | null} CFS: extra top-level links (Brands) after the departments */
    this.extras = this.querySelector('template[data-cfs-mm-extras]');

    for (const trigger of document.querySelectorAll('[data-cfs-mobile-menu-trigger], [data-cfs-aisles-trigger]')) {
      trigger.addEventListener('click', this.#onTriggerClick);
    }
    this.back.addEventListener('click', () => this.#goBack());
    this.querySelector('[data-cfs-mm-close]')?.addEventListener('click', () => this.close());
    // CFS: set here, not in the markup, so a browser without popover support keeps the plain
    // dialog (and never meets the popover polyfill, which loads lazily). aria-modal is only
    // needed once the dialog is no longer modal.
    if (this.#popover) {
      this.dialog.setAttribute('popover', 'auto');
      this.dialog.setAttribute('aria-modal', 'true');
    }
    this.dialog.addEventListener('close', this.#onClose); // showModal() fallback
    this.dialog.addEventListener('toggle', this.#onToggle); // popover: Esc, back gesture, light dismiss, close()
    this.list.addEventListener('click', this.#onListClick);
    this.dialog.addEventListener('keydown', this.#onKeydown);
    MOBILE.addEventListener('change', () => !MOBILE.matches && this.close());
  }

  disconnectedCallback() {
    this.#restoreBackground(); // e.g. a theme editor re-render while the menu is open
  }

  /** @param {MouseEvent} event */
  #onTriggerClick = (event) => {
    if (!MOBILE.matches) return; // desktop: the Browse aisles panel handles it
    event.preventDefault();
    this.open(/** @type {HTMLElement} */ (event.currentTarget));
  };

  /** @param {HTMLElement | null} [trigger] */
  open(trigger = null) {
    this.#trigger = trigger;
    this.#stack = [{ level: 0 }];
    if (this.#popover) this.dialog.showPopover();
    else this.dialog.showModal();
    document.documentElement.classList.add('cfs-mm-open');
    trigger?.setAttribute('aria-expanded', 'true');
    this.#render(); // moves focus into the menu
    if (this.#popover) this.#hideBackground();
  }

  close() {
    if (this.#popover && this.dialog.matches(':popover-open')) this.dialog.hidePopover();
    else if (this.dialog.open) this.dialog.close();
  }

  /**
   * The native 'close' event does not fire for a popover; 'toggle' does, however it was closed.
   * @param {Event} event
   */
  #onToggle = (event) => {
    if (/** @type {ToggleEvent} */ (event).newState === 'closed') this.#onClose();
  };

  #onClose = () => {
    this.#restoreBackground(); // before focus returns to the trigger, which sits in the hidden page
    document.documentElement.classList.remove('cfs-mm-open');
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#trigger?.focus();
  };

  /**
   * A popover does not make the page inert, and Chrome ignores aria-modal for hiding it, so a
   * screen reader could swipe out of the menu into the page. Hides every sibling around the menu
   * (up to <body>) with aria-hidden while it is open: as cheap as a popover, unlike inert or
   * visibility:hidden, which cost as much as showModal(). Elements that were already hidden are
   * left alone. The compact header bar is hidden too, but cfs-header.js owns its aria-hidden: on
   * close it gets the value that script would give it now, not a remembered one.
   */
  #hideBackground() {
    this.#restoreBackground();
    for (let node = /** @type {Element} */ (this); node.parentElement && node !== document.body; node = node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node || NOT_PAGE_CONTENT.test(sibling.tagName)) continue;
        if (sibling.hasAttribute('data-cfs-compact')) {
          this.#compact = sibling;
          sibling.setAttribute('aria-hidden', 'true');
          continue;
        }
        const previous = sibling.getAttribute('aria-hidden');
        if (previous === 'true') continue;
        this.#hidden.push([sibling, previous]);
        sibling.setAttribute('aria-hidden', 'true');
      }
    }
  }

  #restoreBackground() {
    for (const [element, previous] of this.#hidden) {
      if (previous === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', previous);
    }
    this.#hidden = [];
    if (this.#compact) {
      // CFS: cfs-header.js sets aria-hidden to String(!condensed) on the compact bar; read it again.
      const condensed = this.#compact.closest('cfs-header')?.classList.contains('cfs-header--condensed') ?? false;
      this.#compact.setAttribute('aria-hidden', String(!condensed));
      this.#compact = null;
    }
  }

  /** Departments from the panel markup. @returns {MenuLink[]} */
  #departments() {
    return [...document.querySelectorAll('cfs-aisles-panel a[data-department-index]')].map((a) => ({
      title: a.textContent?.trim() ?? '',
      href: /** @type {HTMLAnchorElement} */ (a).href,
    }));
  }

  /** @param {number} d @returns {MenuLink[]} */
  #aisles(d) {
    return [...document.querySelectorAll(`cfs-aisles-panel [data-department-aisles="${d}"] a[data-aisle-index]`)].map((a) => ({
      title: a.textContent?.trim() ?? '',
      href: /** @type {HTMLAnchorElement} */ (a).href,
    }));
  }

  /** @param {number} d @param {number} a @returns {Promise<MenuLink[] | null>} */
  async #shelves(d, a) {
    const department = this.#departments()[d - 1];
    if (!department) return null;
    const detail = await loadDepartmentDetail(department.href);
    if (!detail) return null;
    return [...detail.querySelectorAll(`[data-aisle-index="${a}"] .cfs-aisles__list a`)].map((link) => ({
      title: link.textContent?.trim() ?? '',
      href: /** @type {HTMLAnchorElement} */ (link).href,
    }));
  }

  /**
   * Keeps Tab inside the menu (a popover otherwise lets focus run on into the page behind it, and a
   * modal dialog into the browser UI).
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const items = /** @type {HTMLElement[]} */ (
      [...this.dialog.querySelectorAll('a[href], button:not([disabled])')].filter((el) => el.getClientRects().length > 0)
    );
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === this.titleEl)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** @param {MouseEvent} event */
  #onListClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('button[data-drill]');
    if (!button) return;
    const state = this.#stack[this.#stack.length - 1];
    const index = Number(button.dataset.drill);
    if (state.level === 0) this.#stack.push({ level: 1, department: index, opener: button });
    else if (state.level === 1) this.#stack.push({ level: 2, department: state.department, aisle: index, opener: button });
    this.#render();
  };

  #goBack() {
    if (this.#stack.length < 2) return;
    const left = /** @type {MenuState} */ (this.#stack.pop());
    this.#render(left);
  }

  /** @param {MenuState} [returningFrom] */
  async #render(returningFrom) {
    const renderId = ++this.#renderId;
    const state = this.#stack[this.#stack.length - 1];
    const departments = this.#departments();
    const department = state.department ? departments[state.department - 1] : undefined;
    const aisle = state.department && state.aisle ? this.#aisles(state.department)[state.aisle - 1] : undefined;

    // Bar, title, trail, "Shop all"
    this.back.hidden = state.level === 0;
    this.backLabel.textContent = state.level === 1 ? 'All departments' : department?.title ?? 'Back';
    this.titleEl.textContent = state.level === 0 ? 'Browse aisles' : state.level === 1 ? department?.title ?? '' : aisle?.title ?? '';
    this.trail.textContent =
      state.level === 0 ? `${departments.length} departments` : state.level === 1 ? 'Browse aisles ›' : `Browse aisles › ${department?.title} ›`;
    const allTarget = state.level === 1 ? department : state.level === 2 ? aisle : undefined;
    this.all.hidden = !allTarget;
    if (allTarget) {
      this.all.href = allTarget.href;
      this.allLabel.textContent = `Shop all ${allTarget.title}`;
    }

    // Rows
    /** @type {MenuLink[] | null} */
    let rows;
    if (state.level === 0) rows = departments;
    else if (state.level === 1) rows = this.#aisles(/** @type {number} */ (state.department));
    else {
      this.list.replaceChildren();
      this.skeleton.hidden = false;
      rows = await this.#shelves(/** @type {number} */ (state.department), /** @type {number} */ (state.aisle));
      if (renderId !== this.#renderId) return; // user moved on
      this.skeleton.hidden = true;
    }

    const drill = state.level < 2;
    this.list.replaceChildren(
      ...(rows ?? []).map((row, i) => {
        const li = document.createElement('li');
        if (drill) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'cfs-mm__row';
          button.dataset.drill = String(i + 1);
          button.innerHTML =
            '<span></span><svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>';
          /** @type {HTMLElement} */ (button.firstElementChild).textContent = row.title;
          li.append(button);
        } else {
          const link = document.createElement('a');
          link.className = 'cfs-mm__row';
          link.href = row.href;
          link.textContent = row.title;
          li.append(link);
        }
        return li;
      })
    );
    if (state.level === 0 && this.extras) this.list.append(this.extras.content.cloneNode(true));
    if (rows === null) {
      const li = document.createElement('li');
      li.className = 'cfs-mm__error';
      li.textContent = 'Sorry, this aisle could not load. Use "Shop all" above.';
      this.list.append(li);
    }
    this.querySelector('.cfs-mm__body')?.scrollTo(0, 0);

    // Focus: back to the row we came from, else the level title.
    if (returningFrom?.opener) {
      const index = returningFrom.level === 1 ? returningFrom.department : returningFrom.aisle;
      /** @type {HTMLElement | null} */ (this.list.querySelector(`[data-drill="${index}"]`))?.focus();
    } else {
      this.titleEl.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('cfs-mobile-menu')) customElements.define('cfs-mobile-menu', CfsMobileMenu);
